import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Linkedin, Twitter, Github, Instagram } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const columns = [
  {
    title: "Services",
    links: [
      { label: "Website Development", to: "/services/website-development" },
      { label: "Custom Web Apps", to: "/services/custom-web-applications" },
      { label: "Mobile Apps", to: "/services/mobile-app-development" },
      { label: "AI Automation", to: "/services/ai-automation" },
      { label: "SEO", to: "/services/seo" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "About", to: "/about" },
      { label: "Portfolio", to: "/portfolio" },
      { label: "Careers", to: "/careers" },
      { label: "Blog", to: "/blog" },
      { label: "Contact", to: "/contact" },
    ],
  },
  {
    title: "Resources",
    links: [
      { label: "Pricing", to: "/pricing" },
      { label: "FAQ", to: "/faq" },
      { label: "Sitemap", to: "/sitemap" },
      { label: "Privacy Policy", to: "/privacy" },
      { label: "Terms & Conditions", to: "/terms" },
    ],
  },
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { data } = await api.post("/newsletter", { email });
      toast.success(data.message);
      setEmail("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="relative border-t border-white/10 bg-ink-950" data-testid="footer">
      <div className="container-wv py-20">
        <div className="grid gap-12 lg:grid-cols-[1.4fr_1fr_1fr_1fr]">
          <div>
            <Link to="/" className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-vantage font-heading text-lg font-black text-white">
                W
              </span>
              <span className="font-heading text-lg font-bold tracking-tight">
                Web Vantage<span className="text-vantage">.</span>
              </span>
            </Link>
            <p className="mt-5 max-w-sm text-sm leading-relaxed text-slate-400">
              A premium digital agency engineering award-winning websites, custom applications,
              AI automation and SEO that drive measurable growth.
            </p>
            <form onSubmit={subscribe} className="mt-6 flex max-w-sm items-center gap-2" data-testid="newsletter-form">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                data-testid="newsletter-input"
                className="h-11 flex-1 rounded-full border border-white/10 bg-white/5 px-4 text-sm text-white placeholder:text-slate-500 focus:border-vantage focus:outline-none"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="newsletter-submit"
                className="flex h-11 w-11 items-center justify-center rounded-full bg-vantage text-white transition-colors hover:bg-vantage-hover disabled:opacity-60"
                aria-label="Subscribe"
              >
                <ArrowRight className="h-5 w-5" />
              </button>
            </form>
          </div>

          {columns.map((col) => (
            <div key={col.title}>
              <h4 className="font-heading text-sm font-semibold uppercase tracking-wider text-white">{col.title}</h4>
              <ul className="mt-5 space-y-3">
                {col.links.map((l) => (
                  <li key={l.to}>
                    <Link to={l.to} className="text-sm text-slate-400 transition-colors hover:text-vantage">
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-16 flex flex-col items-center justify-between gap-6 border-t border-white/10 pt-8 sm:flex-row">
          <p className="text-sm text-slate-500">© {new Date().getFullYear()} Web Vantage Enterprises. All rights reserved.</p>
          <div className="flex items-center gap-3">
            {[
              { Icon: Linkedin, href: "https://www.linkedin.com/company/web-vantage-enterprises" },
              { Icon: Twitter, href: "https://twitter.com/webvantage" },
              { Icon: Github, href: "https://github.com/webvantage" },
              { Icon: Instagram, href: "https://www.instagram.com/webvantage" },
            ].map(({ Icon, href }, i) => (
              <a
                key={i}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 text-slate-400 transition-colors hover:border-vantage hover:text-vantage"
                aria-label="Social link"
              >
                <Icon className="h-4 w-4" />
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
